import styled from 'styled-components'
import {api} from 'lib/url'
import {useOrganization} from 'organization/OrganizationProvider'
import React, {useCallback, useEffect, useState} from 'react'
import GridCard from 'organization/EventList/Cards/GridCard'
import ListCard from 'organization/EventList/Cards/ListCard'
import Page from 'lib/ui/layout/Page'
import {ObvioEvent} from 'Event'
import Layout from 'organization/user/Layout'
import {useBreadcrumbs} from 'lib/ui/BreadcrumbProvider'
import {usePageView} from 'analytics'
import Title from 'lib/ui/PageHeader/Title'
import {CREATE_EVENTS} from 'organization/PermissionsProvider'
import HasPermission from 'organization/HasPermission'
import NewItemButton from 'lib/ui/PageHeader/NewItemButton'
import {useIsOwner} from 'organization/OwnerProvider'
import {useToggleArray} from 'lib/toggle'
import Button from 'lib/ui/Button'
import {
  ToggleViewIcon,
  DesktopPageHeader,
  MobilePageHeader,
  HeaderLink,
} from 'lib/ui/GridCollection'
import {PerPageSelector} from 'obvio/Organizations/index'
import Pagination from 'lib/ui/Pagination'
import Search from 'organization/EventList/Search'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'
import {PaginatedCollection} from 'lib/ui/api-client'
import {teamMemberClient} from 'obvio/obvio-client'
import {useInterval} from 'lib/interval'
import useDebounce from 'lib/debounce'
import ShowArchivedButton from 'organization/EventList/ShowArchivedButton'
import {PerPageOption} from 'obvio/Organizations/UserOrganizationsProvider'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'

const PER_PAGE = 12
type EventsPerPage = PerPageOption

/**
 * The events list doesn't need all of the Event's attributes, or
 * relations, so we'll just return the minimum required here to
 * speed up load times.
 */
export type ListEventData = Pick<
  ObvioEvent,
  | 'id'
  | 'end'
  | 'logo'
  | 'name'
  | 'num_expected_attendees'
  | 'slug'
  | 'start'
  | 'timezone'
  | 'system_domain_id'
  | 'is_copying'
  | 'is_deleting'
  | 'is_online'
  | 'is_live'
  | 'is_archived'
>

export default function EventList() {
  const {organization, routes} = useOrganization()
  const isOwner = useIsOwner()
  const [showingListView, toggleListView] = useToggleArray()

  const [events, setEvents] = useState<ObvioEvent[] | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 400)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<EventsPerPage>(PER_PAGE)
  const [lastPage, setLastPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const clearError = () => setError(null)

  const onArchivedChange = useCallback((archived: boolean) => {
    setShowArchived(archived)
    setPage(1)
  }, [])

  const onSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query)
    setPage(1)
  }, [])

  useEffect(() => {
    setLoading(true)
    listEvents(
      page,
      perPage,
      debouncedSearchQuery,
      organization.id,
      showArchived,
    )
      .then((events) => {
        clearError()
        setEventsData(events)
      })
      .catch(() => {
        setError('An error occurred while fetching events')
        setEvents([])
      })
      .finally(() => {
        setLoading(false)
      })
  }, [page, perPage, debouncedSearchQuery, organization.id, showArchived])

  const fetchEvents = useCallback(() => {
    listEvents(
      page,
      perPage,
      debouncedSearchQuery,
      organization.id,
      showArchived,
    )
      .then((events) => {
        clearError()
        setEventsData(events)
      })
      .catch((error) => {
        setError(error.message)
      })
  }, [page, perPage, debouncedSearchQuery, organization.id, showArchived])

  const setEventsData = (events: PaginatedCollection<ObvioEvent>) => {
    setEvents(events.data)
    setLastPage(events.last_page)
    setPage(events.current_page)
  }

  const hasEventBeingCopied =
    events !== null && events.some((event) => event.is_copying)
  useInterval(fetchEvents, 3000, !hasEventBeingCopied)

  const onDelete = (target: {id: number}) => {
    setEvents((events) => {
      if (!events) {
        return null
      }
      return events.map((event) =>
        event.id === target.id ? {...event, is_deleting: true} : event,
      )
    })
  }

  const onArchive = (target: {id: number}) => {
    setEvents((events) => {
      if (!events) {
        return null
      }
      return events.filter((event) => event.id !== target.id)
    })
  }

  const onUnarchive = (target: {id: number}) => {
    setEvents((events) => {
      if (!events) {
        return null
      }
      return events.filter((event) => event.id !== target.id)
    })
  }

  usePageView()

  const canAccessSettings = isOwner
  const homeLink = canAccessSettings ? routes.settings : routes.events.root

  useBreadcrumbs([
    {title: organization.name, url: homeLink},
    {title: 'Events', url: routes.events.root},
  ])

  if (events === null) {
    return <FullPageLoader />
  }

  return (
    <Layout>
      <Page>
        <DesktopPageHeader>
          <TitleBar>
            <Title text="Events" />
            <ShowArchivedButton
              showArchived={showArchived}
              onChange={onArchivedChange}
            />
          </TitleBar>
          <Container>
            <Search
              searchQuery={searchQuery}
              setSearchQuery={onSearchQueryChange}
            />
            <Button onClick={toggleListView} aria-label="toggle view">
              <ToggleViewIcon isListView={showingListView} />
            </Button>
            <HasPermission required={CREATE_EVENTS}>
              <HeaderLink disableStyles to={routes.events.create}>
                <StyledNewItemButton
                  text="New Event"
                  aria-label="create event"
                />
              </HeaderLink>
            </HasPermission>
          </Container>
        </DesktopPageHeader>
        <MobilePageHeader>
          <MobileTitleBar>
            <Title text="Events" />
            <ShowArchivedButton
              showArchived={showArchived}
              onChange={onArchivedChange}
            />
          </MobileTitleBar>
          <HasPermission required={CREATE_EVENTS}>
            <HeaderLink disableStyles to={routes.events.create}>
              <StyledNewItemButton text="New Event" aria-label="create event" />
            </HeaderLink>
          </HasPermission>
          <Container>
            <Search
              searchQuery={searchQuery}
              setSearchQuery={onSearchQueryChange}
            />
            <Button onClick={toggleListView} aria-label="toggle view">
              <ToggleViewIcon isListView={showingListView} />
            </Button>
          </Container>
        </MobilePageHeader>

        <StyledErrorAlert onClose={clearError}>{error}</StyledErrorAlert>

        <Content
          events={events}
          searchQuery={searchQuery}
          showingListView={showingListView}
          showArchived={showArchived}
          onDelete={onDelete}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          loading={loading}
        />
        <PaginationWrap>
          <Pagination
            count={lastPage}
            page={page}
            disabled={loading}
            onChange={(nextPage) => {
              setPage(nextPage)
              window.scrollTo(0, 0)
            }}
          />
          <PerPageSelector
            perPage={perPage}
            disabled={loading}
            ariaLabel="select events per page"
            onChange={(nextPerPage) => {
              setPage(1)
              setPerPage(nextPerPage)
              window.scrollTo(0, 0)
            }}
          />
        </PaginationWrap>
      </Page>
    </Layout>
  )
}

function listEvents(
  page: number,
  perPage: EventsPerPage,
  searchQuery: string,
  organizationId: number,
  archived: boolean,
) {
  const params = new URLSearchParams()
  params.set('page', `${page}`)
  params.set('per_page', `${perPage}`)
  const trimmed = searchQuery.trim()
  if (trimmed) {
    params.set('search', trimmed)
  }
  if (archived) {
    params.set('status', 'archived')
  }

  return teamMemberClient.get<PaginatedCollection<ObvioEvent>>(
    api(`/organizations/${organizationId}/events?${params.toString()}`),
  )
}

function Content(props: {
  events: ListEventData[]
  searchQuery: string
  showingListView: boolean
  showArchived: boolean
  onDelete: (event: {id: number}) => void
  onArchive: (event: {id: number}) => void
  onUnarchive: (event: {id: number}) => void
  loading: boolean
}) {
  const {
    events,
    searchQuery,
    showingListView,
    showArchived,
    onDelete,
    onArchive,
    onUnarchive,
    loading,
  } = props
  const isEmpty = events.length === 0
  if (isEmpty && searchQuery !== '') {
    return <EmptySearchResultsMessage />
  }
  if (isEmpty && showArchived) {
    return <EmptyArchivedEventsMessage />
  }
  if (isEmpty) {
    return <EmptyCreatedEventsMessage />
  }

  if (showingListView) {
    return (
      <LoadingContent $loading={loading}>
        {events.map((e, idx) => (
          <ListCard
            key={e.id}
            event={e}
            index={idx}
            onDelete={onDelete}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
          />
        ))}
      </LoadingContent>
    )
  }

  return (
    <LoadingContent $loading={loading}>
      <Grid>
        {events.map((e) => (
          <GridCard
            key={e.id}
            event={e}
            onDelete={onDelete}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
          />
        ))}
      </Grid>
    </LoadingContent>
  )
}

function EmptySearchResultsMessage() {
  return (
    <EmptyBox>
      <p>No events match your search</p>
    </EmptyBox>
  )
}

function EmptyCreatedEventsMessage() {
  return (
    <EmptyBox>
      <p>No events have been created</p>
    </EmptyBox>
  )
}

function EmptyArchivedEventsMessage() {
  return (
    <EmptyBox>
      <p>No archived events</p>
    </EmptyBox>
  )
}

const column = `minmax(270px, auto)`

const LoadingContent = styled.div<{$loading: boolean}>`
  opacity: ${(props) => (props.$loading ? 0.5 : 1)};
  pointer-events: ${(props) => (props.$loading ? 'none' : 'auto')};
  transition: opacity 200ms;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto;
  grid-gap: ${(props) => props.theme.spacing[8]};

  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    grid-template-columns: ${column} ${column};
    grid-template-rows: auto auto;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: ${column} ${column} ${column};
    grid-template-rows: auto auto auto;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-template-columns: ${column} ${column} ${column} ${column};
    grid-template-rows: auto auto auto auto;
  }
`

const TitleBar = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing[6]};
  justify-content: flex-start;
`

const MobileTitleBar = styled(TitleBar)`
  justify-content: center;
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing[2]};
`

const StyledNewItemButton = styled(NewItemButton)`
  color: #ffffff !important;
`

const StyledErrorAlert = styled(ErrorAlert)`
  margin-top: -${(props) => props.theme.spacing[14]};

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    margin-top: -${(props) => props.theme.spacing[2]};
  }
`

const EmptyBox = styled.div`
  margin-top: ${(props) => props.theme.spacing[8]};
  text-align: center;
`
const PaginationWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: ${(props) => props.theme.spacing[8]};
  margin-bottom: ${(props) => props.theme.spacing[4]};

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    gap: ${(props) => props.theme.spacing[4]};
  }
`
